import { hlInfo } from '../hyperliquidInfo';

import type {
    Bar,
    DatafeedConfiguration,
    DatafeedErrorCallback,
    HistoryCallback,
    IBasicDataFeed,
    LibrarySymbolInfo,
    OnReadyCallback,
    PeriodParams,
    ResolutionString,
    ResolveCallback,
    SearchSymbolsCallback,
    SubscribeBarsCallback,
    SymbolResolveExtension,
} from './charting_library';


/**
 * TradingView datafeed backed by Hyperliquid's public info API
 * (`candleSnapshot`) — the same venue the speed backend trades on, so the
 * candles match the prices users get filled at. CORS is open (`*`), no auth.
 */


/** TradingView resolution → Hyperliquid candle interval. */
const RESOLUTION_TO_INTERVAL: Record<string, string> = {
    '1': '1m',
    '5': '5m',
    '15': '15m',
    '60': '1h',
    '240': '4h',
    '1D': '1d',
};

const RESOLUTION_SECONDS: Record<string, number> = {
    '1': 60,
    '5': 300,
    '15': 900,
    '60': 3600,
    '240': 14400,
    '1D': 86400,
};

const SUPPORTED_RESOLUTIONS = Object.keys(
    RESOLUTION_TO_INTERVAL,
) as ResolutionString[];

const CONFIG: DatafeedConfiguration = {
    supported_resolutions: SUPPORTED_RESOLUTIONS,
    exchanges: [],
    symbols_types: [],
};

interface HlCandle {
    t: number; // open time ms
    o: string;
    h: string;
    l: string;
    c: string;
    v: string;
}

/**
 * `timeoutMs` is generous rather than absent: this read had no ceiling at all,
 * and a request the network drops without rejecting would otherwise hang its
 * caller forever. At 15s it only ever fires on a genuinely dead request.
 */
const CANDLES_TIMEOUT_MS = 15_000;

export async function fetchCandles(
    coin: string,
    interval: string,
    startTime: number,
    endTime: number,
    signal?: AbortSignal,
): Promise<Bar[]> {
    const candles = await hlInfo<HlCandle[]>(
        {
            type: 'candleSnapshot',
            req: { coin, interval, startTime, endTime },
        },
        { signal, timeoutMs: CANDLES_TIMEOUT_MS },
    );
    return candles.map((c) => ({
        time: c.t,
        open: Number(c.o),
        high: Number(c.h),
        low: Number(c.l),
        close: Number(c.c),
        volume: Number(c.v),
    }));
}

/** Price scale giving ~5 significant digits for the asset's magnitude. */
function priceScaleFor(referencePrice: number): number {
    if (!Number.isFinite(referencePrice) || referencePrice <= 0) return 100;
    if (referencePrice >= 10000) return 10;
    if (referencePrice >= 100) return 100;
    if (referencePrice >= 1) return 10000;
    if (referencePrice >= 0.01) return 1000000;
    return 100000000;
}

interface DatafeedDeps {
    getCoin: () => string;
    getReferencePrice: () => number;
}

export interface HlDatafeed extends IBasicDataFeed {
    /** Feed a live mark-price tick into the currently building bar. */
    tickPrice: (price: number) => void;
    destroy: () => void;
}

interface Subscriber {
    coin: string;
    resolution: string;
    callback: SubscribeBarsCallback;
    lastBar: Bar | null;
    /** Realtime bars may never go backwards past the newest history bar. */
    minTime: number;
}

export function createHlDatafeed(deps: DatafeedDeps): HlDatafeed {
    const subscribers = new Map<string, Subscriber>();
    // Newest bar time served via getBars, per coin|resolution — HL history
    // includes the in-progress candle, so realtime pushes older than this
    // would violate TV's time ordering.
    const lastHistoryTime = new Map<string, number>();

    // Reconcile the building bar against the venue every few seconds; live
    // WS ticks (tickPrice) keep it moving in between.
    const pollId = window.setInterval(() => {
        for (const sub of subscribers.values()) {
            const interval = RESOLUTION_TO_INTERVAL[sub.resolution];
            if (!interval) continue;
            const spanMs = (RESOLUTION_SECONDS[sub.resolution] ?? 60) * 1000;
            const now = Date.now();
            fetchCandles(sub.coin, interval, now - spanMs * 2, now)
                .then((bars) => {
                    for (const bar of bars) {
                        if (bar.time < sub.minTime) continue;
                        sub.lastBar = bar;
                        sub.minTime = bar.time;
                        sub.callback(bar);
                    }
                })
                .catch(() => {
                    // transient poll failure — the next tick retries
                });
        }
    }, 5000);

    return {
        onReady(callback: OnReadyCallback) {
            setTimeout(() => callback(CONFIG), 0);
        },

        searchSymbols(
            _userInput: string,
            _exchange: string,
            _symbolType: string,
            onResult: SearchSymbolsCallback,
        ) {
            // Market switching happens in the app (sidebar / ⌘K), not in TV.
            onResult([]);
        },

        resolveSymbol(
            symbolName: string,
            onResolve: ResolveCallback,
            _onError: DatafeedErrorCallback,
            _extension?: SymbolResolveExtension,
        ) {
            const coin = symbolName || deps.getCoin();
            const symbolInfo: LibrarySymbolInfo = {
                name: coin,
                ticker: coin,
                description: `${coin} / USD`,
                type: 'crypto',
                session: '24x7',
                timezone: 'Etc/UTC',
                exchange: 'Hyperliquid',
                listed_exchange: 'Hyperliquid',
                format: 'price',
                pricescale: priceScaleFor(deps.getReferencePrice()),
                minmov: 1,
                has_intraday: true,
                supported_resolutions: SUPPORTED_RESOLUTIONS,
                intraday_multipliers: ['1', '5', '15', '60', '240'],
                has_daily: true,
                volume_precision: 2,
                data_status: 'streaming',
            };
            setTimeout(() => onResolve(symbolInfo), 0);
        },

        getBars(
            symbolInfo: LibrarySymbolInfo,
            resolution: ResolutionString,
            periodParams: PeriodParams,
            onResult: HistoryCallback,
            onError: DatafeedErrorCallback,
        ) {
            const interval = RESOLUTION_TO_INTERVAL[resolution as string];
            if (!interval) {
                onResult([], { noData: true });
                return;
            }
            const coin = symbolInfo.ticker ?? deps.getCoin();
            const { from, to } = periodParams;
            fetchCandles(coin, interval, from * 1000, to * 1000)
                .then((bars) => {
                    if (bars.length > 0) {
                        const key = `${coin}|${resolution}`;
                        const newest = bars[bars.length - 1]!.time;
                        lastHistoryTime.set(
                            key,
                            Math.max(lastHistoryTime.get(key) ?? 0, newest),
                        );
                    }
                    onResult(bars, { noData: bars.length === 0 });
                })
                .catch((error: unknown) => {
                    onError(
                        error instanceof Error
                            ? error.message
                            : 'candles unavailable',
                    );
                });
        },

        subscribeBars(
            symbolInfo: LibrarySymbolInfo,
            resolution: ResolutionString,
            onTick: SubscribeBarsCallback,
            listenerGuid: string,
            _onResetCacheNeededCallback: () => void,
        ) {
            const coin = symbolInfo.ticker ?? deps.getCoin();
            subscribers.set(listenerGuid, {
                coin,
                resolution: resolution as string,
                callback: onTick,
                lastBar: null,
                minTime: lastHistoryTime.get(`${coin}|${resolution}`) ?? 0,
            });
        },

        unsubscribeBars(listenerGuid: string) {
            subscribers.delete(listenerGuid);
        },

        tickPrice(price: number) {
            if (!Number.isFinite(price) || price <= 0) return;
            const now = Date.now();
            for (const sub of subscribers.values()) {
                const spanMs =
                    (RESOLUTION_SECONDS[sub.resolution] ?? 60) * 1000;
                const bucket = Math.floor(now / spanMs) * spanMs;
                if (bucket < sub.minTime) continue;

                let bar = sub.lastBar;
                if (!bar || bucket > bar.time) {
                    // New bucket — open at the previous close if we have one.
                    bar = {
                        time: bucket,
                        open: bar?.close ?? price,
                        high: price,
                        low: price,
                        close: price,
                    };
                } else {
                    bar = {
                        ...bar,
                        high: Math.max(bar.high, price),
                        low: Math.min(bar.low, price),
                        close: price,
                    };
                }
                sub.lastBar = bar;
                sub.minTime = bar.time;
                sub.callback(bar);
            }
        },

        destroy() {
            window.clearInterval(pollId);
            subscribers.clear();
        },
    } as HlDatafeed;
}
